import { AuthContextController, useMutation } from 'shared-ui';
import { PropsWithChildren, useEffect } from 'react';

import { onLogin, onLogout } from 'api/auth/auth';
import { JwtPayload } from 'api/auth/auth.types';
import { Functionality } from 'api/userPermissions/userPermissions.enum';
import { fetchLoggedUserDetails } from 'api/users/users';
import { QueryKeyEnum } from 'core/global.enum';
import { useUpdatePasswordModal } from 'hooks/useUpdatePasswordModal/useUpdatePasswordModal';
import { useLicenseModal } from 'hooks/useLicenseModal/useLicenseModal';
import { usePermissions } from 'hooks/usePermissions/usePermissions';

const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const AuthProvider = ({
  children,
  storageType = 'localStorage',
}: PropsWithChildren<{ storageType?: 'localStorage' | 'sessionStorage' }>) => {
  const { mutateAsync } = useMutation(onLogout);
  const { handleUpdatePasswordModalOpen } = useUpdatePasswordModal();
  const { handleLicenseModalOpen } = useLicenseModal();
  const { setPerms, setAccessFlags } = usePermissions();

  const setPermissionsFromToken = (token: string) => {
    const jwt = decodeJwtPayload(token);
    if (!jwt) {
      console.error('Failed to decode JWT');
      return;
    }
    setPerms(jwt.perms);
    setAccessFlags({
      accessAllData: !!jwt.accessAllData,
      accessFeaturedCards: !!jwt.accessFeaturedCards,
      accessNoOrganizations: !!jwt.accessNoOrganizations,
    });
  };

  const setLicencjaOnlyPerms = (token: string) => {
    const jwt = decodeJwtPayload(token);
    const licencjaPerm = jwt?.perms?.[Functionality.LICENCJA];
    setPerms(licencjaPerm ? { [Functionality.LICENCJA]: licencjaPerm } : {});
    setAccessFlags({
      accessAllData: false,
      accessFeaturedCards: false,
      accessNoOrganizations: false,
    });
  };

  // On mount, check for existing token and set permissions
  useEffect(() => {
    const storage = storageType === 'sessionStorage' ? sessionStorage : localStorage;
    const accessToken = storage.getItem('accessToken');
    const storedLicenseStatus = storage.getItem('licenseStatus');

    if (accessToken) {
      const isLicenseInvalid = storedLicenseStatus === 'EXPIRED' || storedLicenseStatus === 'NOT_FOUND';

      if (isLicenseInvalid) {
        handleLicenseModalOpen(true, storedLicenseStatus as 'EXPIRED' | 'NOT_FOUND');
        setLicencjaOnlyPerms(accessToken);
      } else {
        setPermissionsFromToken(accessToken);
      }
    }
  }, [storageType]);

  return (
    <AuthContextController
      onLogout={mutateAsync}
      //TODO poprawić gdy beda gotowe uprawienia
      getIsAdmin={(user) => true}
      onLogin={onLogin}
      fetchUser={fetchLoggedUserDetails}
      fetchUserKey={QueryKeyEnum.LOGGED_USER_DETAILS}
      storageType={storageType}
      onLoginSuccess={(res) => {
        const storage = storageType === 'sessionStorage' ? sessionStorage : localStorage;

        if (res?.isUpdatePasswordRequired) {
          handleUpdatePasswordModalOpen(true, res.user);
        }

        const isLicenseInvalid = res?.licenseStatus === 'EXPIRED' || res?.licenseStatus === 'NOT_FOUND';

        // Store license status for persistence across refreshes
        if (res?.licenseStatus) {
          storage.setItem('licenseStatus', res.licenseStatus);
        } else {
          storage.removeItem('licenseStatus');
        }

        if (isLicenseInvalid && res.licenseStatus) {
          handleLicenseModalOpen(true, res.licenseStatus as 'EXPIRED' | 'NOT_FOUND');
          setLicencjaOnlyPerms(res.accessToken.token);
        } else {
          setPermissionsFromToken(res.accessToken.token);
        }
      }}
    >
      {children}
    </AuthContextController>
  );
};
